import { logger } from '@/utils/logger';
/**
 * Console filtering utility to reduce noise from repetitive Sentry warnings
 * Specifically targets "Invalid date type" messages that spam the console
 */

// Store original console methods (bound to logger so `this` is preserved
// after installConsoleFilter() reassigns logger.warn/error/debug)
const originalConsole = {
  warn: logger.warn.bind(logger),
  error: logger.error.bind(logger),
  log: logger.debug.bind(logger),
};

// Track repetitive messages to prevent spam
const messageFrequency = new Map<string, { count: number; timestamp: number }>();
const SPAM_THRESHOLD = 5; // Allow up to 5 instances of the same message
const MESSAGE_TTL = 60000; // Messages older than 1 minute are forgotten

/**
 * Check if a message should be suppressed due to repetition
 */
function shouldSuppressMessage(message: string): boolean {
  // Specific patterns to suppress
  const suppressPatterns = [
    'Invalid date type, converting to string: undefined',
    'overrideMethod @ hook.js:608',
    '⚠️ React Router Future Flag Warning: React Router will begin wrapping state updates in `React.startTransition` in v7',
    // Supabase auth-js navigator lock "steal" races — these are transient
    // cross-tab/concurrent conflicts that our TanStack Query retry resolves.
    "'steal' option",
    'Lock broken by another request',
    'was released because another request stole it',
    'Lock "lock:sb-',
  ];

  const now = Date.now();

  // Clean up old entries on each check (lazy cleanup)
  for (const [key, data] of messageFrequency.entries()) {
    if (now - data.timestamp > MESSAGE_TTL) {
      messageFrequency.delete(key);
    }
  }

  for (const pattern of suppressPatterns) {
    if (message.includes(pattern)) {
      const existing = messageFrequency.get(pattern);
      const count = existing ? existing.count : 0;

      if (count >= SPAM_THRESHOLD) {
        return true; // Suppress after threshold
      }

      messageFrequency.set(pattern, { count: count + 1, timestamp: now });

      // Show a summary message when hitting the threshold
      if (count === SPAM_THRESHOLD - 1) {
        originalConsole.warn(
          `[Console Filter] Suppressing further "${pattern}" messages to reduce noise`,
        );
      }
    }
  }

  return false;
}

/**
 * Filtered console methods
 */
function filteredWarn(...args: any[]) {
  const message = args.join(' ');
  if (!shouldSuppressMessage(message)) {
    originalConsole.warn(...args);
  }
}

function filteredError(...args: any[]) {
  const message = args.join(' ');
  if (!shouldSuppressMessage(message)) {
    originalConsole.error(...args);
  }
}

function filteredLog(...args: any[]) {
  const message = args.join(' ');
  if (!shouldSuppressMessage(message)) {
    originalConsole.log(...args);
  }
}

/**
 * Install console filtering
 */
export function installConsoleFilter() {
  // Install in all environments to prevent infinite loops from Sentry serialization
  logger.warn = filteredWarn;
  logger.error = filteredError;
  logger.debug = filteredLog;

  if (import.meta.env.DEV) {
    logger.debug('[Console Filter] Installed - repetitive Sentry warnings will be suppressed');
  }
}

/**
 * Restore original console methods
 */
export function uninstallConsoleFilter() {
  logger.warn = originalConsole.warn;
  logger.error = originalConsole.error;
  logger.debug = originalConsole.log;

  logger.debug('[Console Filter] Uninstalled - all console messages restored');
}
